import React, { useState, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@material-ui/styles';
import { useHistory } from 'react-router-dom';
import Skeleton from '@material-ui/lab/Skeleton';
import PropTypes from 'prop-types';

import {
    Avatar,
    Tag,
    Text,
    GridContainer,
    GridItem,
    PhoneIcon,
    FilledIconButton,
    NoticeIcon,
    OutlinedButton,
    SkipQueueDialog,
    StyledCard,
    StarIcon,
    Tooltip,
    Progress,
    MessagesIcon,
} from '../../index';

import { setMessagesReceiver } from '../../../redux/stateActions';

export const PatientAPCard = ({
    data,
    queue,
    $avatarSize,
    showDiagnosis,
    skippedButton,
    onAddPrimaryPatient,
    disabledStarButton,
    noticeButton,
    isPrimary,
    loading,
    onClickCall,
    disabled,
}) => {
    const user = useSelector((state) => state.user);

    const theme = useTheme();
    const history = useHistory();
    const dispatch = useDispatch();
    const [showSkipQueueDialog, setShowSkipQueueDialog] = useState(false);

    const skipQueueHandle = (noContact, canceled) => {
        setShowSkipQueueDialog(false);
    };

    const handleClickPatientDetail = () => {
        history.push(`/provider/client/detail/${data.id}`);
    };

    const handleClickMessages = () => {
        dispatch(
            setMessagesReceiver({
                id: data.id,
                avatar: data?.user?.avatar?.url,
                name: data?.user?.fullname,
                isGroup: false,
            })
        );
    };

    return (
        <div style={{ position: 'relative' }}>
            {loading && <Progress />}
            <SkipQueueDialog
                open={showSkipQueueDialog}
                onConfirm={skipQueueHandle}
                onCancel={() => setShowSkipQueueDialog(false)}
            />
            <StyledCard
                variant="outlined"
                padding="8px"
                style={{
                    background: theme.palette.common.shadeGrayGradient,
                    overflow: 'visible',
                }}
                $borderColor="gray6"
            >
                <div
                    style={{
                        display: 'flex',
                        position: 'relative',
                    }}
                >
                    <div style={{ paddingLeft: '8px' }}>
                        {data ? (
                            <Avatar
                                alt={!data?.user ? null : data?.user?.fullname}
                                src={!data?.user ? null : data?.user?.avatar?.url}
                                $avatarSize={$avatarSize}
                                onClick={handleClickPatientDetail}
                                disabled={disabled}
                            />
                        ) : (
                            <Skeleton variant="rect" width={64} height={64} />
                        )}
                    </div>
                    <div style={{ display: 'flex', flexGrow: 1 }}>
                        <GridContainer style={{ paddingLeft: '10px' }}>
                            <GridItem justifyContent="flex-start">
                                {data ? (
                                    <Text
                                        style={{
                                            fontSize: '16px',
                                            maxWidth: '400px',
                                        }}
                                        $bold
                                        $ellipsis
                                        $lineHeight="20px"
                                        onClick={handleClickPatientDetail}
                                        disabled={disabled}
                                    >
                                        {!data?.user ? 'Inactive' : data?.user?.fullname}
                                    </Text>
                                ) : (
                                    <Skeleton width={200} height={30} variant="text" />
                                )}
                            </GridItem>
                            {showDiagnosis && (
                                <GridItem
                                    justifyContent="flex-start"
                                    style={{
                                        alignSelf: 'flex-end',
                                        display: 'flex',
                                        columnGap: '5px',
                                        rowGap: '5px',
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    {data ? (
                                        data.patientTag?.map((tag, index) => (
                                            <Tag key={tag + index}>{tag}</Tag>
                                        ))
                                    ) : (
                                        <Fragment>
                                            <Skeleton width={70} height={30} variant="text" />
                                            <Skeleton
                                                width={70}
                                                height={30}
                                                variant="text"
                                                style={{ marginLeft: '10px' }}
                                            />
                                        </Fragment>
                                    )}
                                </GridItem>
                            )}
                        </GridContainer>
                    </div>
                    <div style={{ display: 'flex', position: 'relative', alignItems: 'center' }}>
                        {skippedButton && (
                            <OutlinedButton
                                $width="140px"
                                onClick={() => setShowSkipQueueDialog(true)}
                                disabled={!data}
                            >
                                Skip queue
                            </OutlinedButton>
                        )}
                        {onAddPrimaryPatient &&
                            ['Provider', 'Provider Admin'].includes(user.staff.staff_role.name) && (
                                <Tooltip
                                    title={
                                        isPrimary ? 'Added Primary Client' : 'Add to Primary Client'
                                    }
                                >
                                    <div>
                                        <FilledIconButton
                                            $backgroundColor={!isPrimary && 'dimBlue'}
                                            onClick={() => onAddPrimaryPatient(data.id)}
                                            disabled={disabledStarButton}
                                            $width="40px"
                                            $height="40px"
                                        >
                                            <StarIcon />
                                        </FilledIconButton>
                                    </div>
                                </Tooltip>
                            )}
                        {onClickCall && (
                            <FilledIconButton
                                $noShadow
                                $backgroundColor="green"
                                disabled={!data || disabled}
                                $width="40px"
                                $height="40px"
                                onClick={onClickCall}
                            >
                                <PhoneIcon fill="white" width="20" height="20" />
                            </FilledIconButton>
                        )}

                        <FilledIconButton
                            $icon
                            disabled={!data || disabled}
                            $backgroundColor="blue2"
                            $width="40px"
                            $height="40px"
                            onClick={handleClickMessages}
                        >
                            <MessagesIcon />
                        </FilledIconButton>

                        {noticeButton && (
                            <Tooltip title="Send notification">
                                <div>
                                    <FilledIconButton
                                        $backgroundColor="purple"
                                        disabled={!data || disabled}
                                    >
                                        <NoticeIcon />
                                    </FilledIconButton>
                                </div>
                            </Tooltip>
                        )}
                    </div>
                </div>
            </StyledCard>
            <Text
                style={{
                    position: 'absolute',
                    left: '0px',
                    top: '55px',
                    background: `${theme.palette.common.pink}`,
                    fontSize: '12px',
                    color: `${theme.palette.common.white}`,
                    borderRadius: '4px',
                    paddingLeft: '6.5px',
                    paddingRight: '6.5px',
                }}
            >
                {queue}
            </Text>
        </div>
    );
};

PatientAPCard.propTypes = {
    data: PropTypes.object,
    queue: PropTypes.string,
    $avatarSize: PropTypes.string,
    showDiagnosis: PropTypes.bool,
    skippedButton: PropTypes.bool,
    onAddPrimaryPatient: PropTypes.func,
    onClickCall: PropTypes.func,
    disabledStarButton: PropTypes.bool,
    socket: PropTypes.object,
    noticeButton: PropTypes.bool,
    isPrimary: PropTypes.bool,
    loading: PropTypes.bool,
};
